import type { CityConfig } from "@/pages/city-template/types";

export const grandIslandConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Grand Island",
  state: "Nebraska",
  stateAbbr: "NE",
  slug: "grand-island",
  route: "/cities/grand-island",
  title: "Emergency Passport Grand Island, NE — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Grand Island, NE — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Grand Island and Hall County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Grand Island, Nebraska",
  heroSubtitle: "Need an urgent passport in central Nebraska? Fast Passport Center helps Grand Island travelers get their passports fast — as fast as 24 hours. No need to drive to a regional agency. A+ BBB rated, registered State Dept. courier serving Hall County and the Tri-Cities region.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Grand%20Island%20Nebraska%20downtown%20streetscape%20with%20historic%20brick%20buildings%20along%20the%20Platte%20River%2C%20central%20Nebraska%20plains%20landscape%20with%20agricultural%20fields%20stretching%20to%20the%20horizon%2C%20golden%20wheat%20fields%20under%20vast%20blue%20sky%2C%20midwestern%20heartland%20charm%20with%20grain%20elevators%20and%20railroad%20tracks%2C%20warm%20amber%20sunset%20tones%2C%20photorealistic%20landscape%20photography&width=1800&height=700&seq=grand-island-hero-01&orientation=landscape",
  heroImageAlt: "Grand Island Nebraska downtown streetscape along the Platte River with central plains horizon",
  geoPosition: "40.9250;-98.3420",
  geoPlacename: "Grand Island, Nebraska",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
    distance: "Approximately 440 miles west — nearest regional passport agency for central Nebraska travelers",
  },
  nearbyCitiesZips: [
    { name: "Grand Island", zips: "68801–68803" },
    { name: "Kearney", zips: "68845, 68847–68849" },
    { name: "Hastings", zips: "68901–68902" },
    { name: "Columbus", zips: "68601–68602" },
    { name: "York", zips: "68467" },
    { name: "Aurora", zips: "68818" },
    { name: "Central City", zips: "68826" },
    { name: "St. Paul", zips: "68873" },
    { name: "Wood River", zips: "68883" },
    { name: "Doniphan", zips: "68832" },
    { name: "Cairo", zips: "68824" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Grand Island in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Grand Island", desc: "FedEx and UPS locations throughout Grand Island and central Nebraska offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport center — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Michael R.",
      location: "Grand Island, NE",
      rating: 5,
      text: "I had a business trip to Germany with only 6 days notice and my passport had expired. Fast Passport Center handled everything. Shipped from the FedEx on South Locust and had my new passport in 3 days. Made my flight to Frankfurt with time to spare. Incredible service for central Nebraska.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia W.",
      location: "Grand Island, NE",
      rating: 5,
      text: "Our family planned a trip to Mexico for spring break and discovered my husband's passport was expired. Fast Passport Center got us a renewal in 5 business days. The process was incredibly smooth — no driving hours to a regional agency. Will absolutely use them again.",
      service: "Passport Renewal",
    },
    {
      name: "David S.",
      location: "Kearney, NE",
      rating: 5,
      text: "Drove into Grand Island from Kearney to ship my documents and it was the easiest passport experience ever. Needed a new passport for my son for an international school trip. Fast Passport Center walked us through the child passport requirements. 7 days door to door.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "gi-main-post",
      name: "Grand Island Post Office",
      type: "USPS Passport Acceptance",
      address: "204 W South Front St",
      city: "Grand Island",
      state: "NE",
      zip: "68801",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Grand Island",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Main passport acceptance facility in Grand Island. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "kearney-post",
      name: "Kearney Post Office",
      type: "USPS Passport Acceptance",
      address: "2401 E Ave",
      city: "Kearney",
      state: "NE",
      zip: "68847",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Kearney — about 40 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient option for Buffalo County and western Hall County residents. Smaller facility — appointments strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "hastings-post",
      name: "Hastings Post Office",
      type: "USPS Passport Acceptance",
      address: "300 N Kansas Ave",
      city: "Hastings",
      state: "NE",
      zip: "68901",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–3:30pm",
      distance: "Hastings — about 25 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility for Adams County and southern Hall County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "hall-county-clerk",
      name: "Hall County Clerk's Office",
      type: "County Passport Acceptance",
      address: "121 S Pine St",
      city: "Grand Island",
      state: "NE",
      zip: "68801",
      phone: "308-385-5080",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Grand Island",
      website: "https://www.hallcountyne.gov/",
      websiteLabel: "Hall County Website",
      notes: "County clerk may offer passport acceptance services. Call ahead to confirm availability as services can vary by county.",
      icon: "ri-government-line",
    },
    {
      id: "columbus-post",
      name: "Columbus Post Office",
      type: "USPS Passport Acceptance",
      address: "1555 24th Ave",
      city: "Columbus",
      state: "NE",
      zip: "68601",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–3:30pm",
      distance: "Columbus — about 65 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Northeast option for Platte County and eastern Nebraska residents near the Columbus area.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Grand Island before international travel?",
      a: "Yes. Through Fast Passport Center, Grand Island travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Grand Island?",
      a: "Fast passport renewal in Grand Island is available through Fast Passport Center in as little as 3 business days with our expedited service. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years.",
    },
    {
      q: "Does Grand Island have its own passport agency?",
      a: "Grand Island, Nebraska does not have a regional passport agency. The closest regional passport center is the Denver Passport Agency in Hot Springs, Arkansas (about 620 miles southeast). Fast Passport Center is the faster and more convenient solution for Grand Island and central Nebraska residents — no 9.5-hour road trip required.",
    },
    {
      q: "What if my passport expires before my international business trip?",
      a: "If your passport expires before your trip, don't wait. Most countries require at least 3–6 months of passport validity beyond your stay. Fast Passport Center can expedite your passport renewal so you don't miss your travel. Many Grand Island professionals at companies like Case IH and JBS travel internationally.",
    },
    {
      q: "Can I expedite a child passport in Grand Island?",
      a: "Yes. Child passport expediting in Grand Island is possible through Fast Passport Center, but child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist and instructions.",
    },
    {
      q: "How long does a standard passport take in Grand Island?",
      a: "If you apply through a standard post office acceptance facility in Grand Island, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "What airports do Grand Island passport holders typically fly from?",
      a: "Central Nebraska Regional Airport (GRI) offers regional flights. For international travel, most Grand Island residents fly from Omaha Eppley Airfield (OMA) about 2.5 hours east, or Kansas City International (MCI) about 4 hours southeast.",
    },
    {
      q: "Can I get a passport renewal near me in Grand Island without visiting an office?",
      a: "Yes. Most Grand Island adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Grand Island", address: "2250 N Webb Rd, Grand Island, NE 68803", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Grand Island", address: "1230 Allen Dr, Grand Island, NE 68803", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Grand Island", address: "3501 S Locust St, Grand Island, NE 68801", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — Grand Island", address: "3333 Ramada Rd, Grand Island, NE 68801", hours: "Mon–Fri 7:30 AM – 6 PM, Sat 9 AM – 3 PM", website: "https://www.fedex.com/" },
  ],
  dmvLocations: [
    {
      name: "Grand Island Driver License Office",
      address: "1312 Sky Park Rd, Grand Island, NE 68801",
      hours: "Mon–Fri 8 AM – 5 PM",
      note: "Main DMV office serving Hall County. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kearney Driver License Office",
      address: "4615 2nd Ave, Kearney, NE 68847",
      hours: "Mon–Fri 8 AM – 5 PM",
      note: "Alternative DMV for Buffalo County and western Nebraska residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "GRI",
      name: "Central Nebraska Regional Airport",
      distance: "Regional airport in Grand Island — limited commercial service",
      address: "3799 Sky Park Rd, Grand Island, NE 68801",
      note: "GRI offers limited regional flights. Most international travelers flying from Grand Island connect through larger hubs.",
      flightUrl: "https://www.flygrandisland.com/",
      flightLabel: "GRI Airport Website",
      featured: false,
    },
    {
      code: "OMA",
      name: "Eppley Airfield — Omaha",
      distance: "Closest major airport — about 140 miles east (2.5 hours)",
      address: "4501 Abbott Dr, Omaha, NE 68110",
      note: "OMA is the closest major airport for Grand Island travelers with domestic and some international connections.",
      flightUrl: "https://www.flyoma.com/",
      flightLabel: "OMA Airport Website",
      featured: true,
    },
    {
      code: "MCI",
      name: "Kansas City International Airport",
      distance: "Full international service — about 280 miles southeast (4 hours)",
      address: "1 International Sq, Kansas City, MO 64153",
      note: "MCI offers the most international flight options for Grand Island travelers. Direct flights to Europe, Mexico, and the Caribbean available.",
      flightUrl: "https://www.flykci.com/",
      flightLabel: "MCI Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from OMA or MCI in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Grand Island and central Nebraska.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Grand Island and central Nebraska travelers renew fast — expired passport before a trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Grand Island", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Grand Island travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Grand Island families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Grand Island residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Grand Island NE", description: "Urgent passport processing for Grand Island, Nebraska travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport Grand Island NE", description: "Expedited passport processing for Grand Island residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal Grand Island NE", description: "Fast passport renewal for Grand Island, NE residents with a previously-issued passport." },
    { name: "Lost Passport Replacement Grand Island NE", description: "Emergency passport replacement service for Grand Island travelers with lost or stolen passports." },
    { name: "Child Passport Grand Island NE", description: "Expedited child passport services for Grand Island families with minors under 16." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Grand Island travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Grand Island runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Grand Island travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Grand Island", description: "Drop off at any FedEx or UPS in Grand Island or central Nebraska — most locations are open weekdays and Saturdays." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you." },
  ],
  processingOptions: [
    {
      speed: "2–9", unit: "Business Days", label: "Fast Passport Center",
      tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Document review included", "Dedicated agent assigned", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5", unit: "Weeks", label: "Fast Passport Center",
      tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Document review included"],
      highlight: false, smartSaver: true,
    },
    {
      speed: "1–2", unit: "Months", label: "Self-Service (Post Office)",
      tag: "Slowest", tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access", "In-person clerk appointment required", "No document review", "No dedicated agent", "Not suitable if travel is within 10 weeks"],
      highlight: false, muted: true,
    },
    {
      speed: "24", unit: "Hours*", label: "Regional Agency Processing",
      tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required", "Life-or-death emergencies only", "Expect to wait all day or return next day"],
      highlight: false, emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots at Denver Passport Agency", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Hot Springs, AR (9.5 hours)", fpc: "Ship documents overnight from Grand Island" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Turnaround Predictability", agency: "Depends on appointment availability — no guarantee", fpc: "Guaranteed processing within selected timeframe" },
    { feature: "Customer Support", agency: "Government clerk — limited availability", fpc: "Dedicated agent assigned to your case with daily updates" },
    { feature: "Travel Required", agency: "Yes — must travel to Denver in person", fpc: "No — ship documents from home or office in Grand Island" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Omaha", route: "/cities/omaha" },
    { name: "Fast passport renewal in Lincoln", route: "/cities/lincoln" },
    { name: "Emergency passport help near Kearney", route: "/cities/kearney" },
    { name: "Fast passport services for all Nebraska", route: "/states/nebraska" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Grand Island or central Nebraska — most are open weekdays and Saturdays. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Grand Island and all of central Nebraska remotely",
  serviceAreaIntro: "Whether you are in Grand Island, Kearney, Hastings, Columbus, or anywhere in Hall County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Grand Island and Hall County zip codes. Anywhere in central Nebraska — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Grand Island as simple as possible. Most Grand Island clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Grand Island travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Grand Island and central Nebraska travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Grand Island and Hall County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/nebraska",
  statePageName: "Nebraska",
};